import { motion } from "framer-motion";
import LogoScene from "@/components/LogoScene";
import { KineticLine } from "@/components/Kinetic";
import Countdown from "@/components/Countdown";
import { scrollToId } from "@/components/Shared";

const PixelChevron = () => (
  <motion.span
    className="inline-flex flex-col items-center gap-[3px]"
    animate={{ y: [0, 7, 0] }}
    transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
    aria-hidden="true"
  >
    <span className="flex gap-[3px]">
      <span className="w-[5px] h-[5px] bg-white/70" />
      <span className="w-[5px] h-[5px] bg-white/70" />
      <span className="w-[5px] h-[5px] bg-white/70" />
    </span>
    <span className="flex gap-[3px]">
      <span className="w-[5px] h-[5px] bg-white/70 ml-[4px]" />
      <span className="w-[5px] h-[5px] bg-white/70" />
    </span>
    <span className="w-[5px] h-[5px] bg-white/70" />
  </motion.span>
);

export default function Hero() {
  return (
    <section id="top" className="relative h-[100svh] min-h-[680px] overflow-hidden">
      <div className="absolute -left-40 top-1/4 w-[34rem] h-[34rem] neon-glow-pink rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute -right-40 bottom-0 w-[30rem] h-[30rem] neon-glow-blue rounded-full blur-3xl" aria-hidden="true" />

      {/* Giant split typography */}
      <div className="absolute top-[13%] sm:top-[11%] left-0 right-0 flex items-start justify-between px-4 sm:px-8 pointer-events-none select-none">
        <KineticLine delay={0.2} className="font-display font-semibold uppercase leading-[0.82] tracking-tight text-[12vw] sm:text-[10vw] text-white">
          Always
        </KineticLine>
        <KineticLine delay={0.45} className="font-display font-semibold uppercase leading-[0.82] tracking-tight text-[12vw] sm:text-[10vw] text-white">
          Forever
        </KineticLine>
      </div>

      {/* Center mark between the words */}
      <motion.div
        className="absolute top-[26%] sm:top-[24%] left-1/2 -translate-x-1/2 flex flex-col items-center gap-2.5 z-20 pointer-events-none"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.9 }}
      >
        <img src="/assets/logo-alpha.png" alt="" className="w-9 h-9 object-contain" aria-hidden="true" />
        <p className="text-[10px] tracking-[0.14em] text-white/60 whitespace-nowrap">( By Monica Alexander )</p>
      </motion.div>

      {/* Side micro-label stacks */}
      <motion.div
        className="hidden md:block absolute left-10 top-[38%] text-[11px] leading-6 text-white/45 font-light"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 1 }}
      >
        <p>Sublimation / Dye-Sublimation</p>
        <p>Hand Airbrush / One of One</p>
        <p>Embroidered Script / Threadwork</p>
        <p>Made in Chicago / Est. A&F</p>
      </motion.div>
      <motion.div
        className="hidden md:flex absolute right-10 top-[38%] flex-col items-end text-[11px] leading-6 text-white/60"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 1 }}
      >
        <button data-testid="hero-link-collection" onClick={() => scrollToId("collection")} className="underline-offset-4 hover:underline hover:text-white transition-colors pointer-events-auto">Script Sets</button>
        <button data-testid="hero-link-varsity" onClick={() => scrollToId("varsity")} className="underline-offset-4 hover:underline hover:text-white transition-colors pointer-events-auto">Varsity Series</button>
        <button data-testid="hero-link-craft" onClick={() => scrollToId("craft")} className="underline-offset-4 hover:underline hover:text-white transition-colors pointer-events-auto">The Craft</button>
        <button data-testid="hero-link-follow" onClick={() => scrollToId("footer")} className="underline-offset-4 hover:underline hover:text-white transition-colors pointer-events-auto">Follow the Drop</button>
      </motion.div>

      {/* 3D embroidered logo */}
      <motion.div
        className="absolute inset-x-0 top-[26%] bottom-[12%] z-10"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <LogoScene />
      </motion.div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-wrap items-end justify-between gap-x-6 gap-y-5 px-5 sm:px-10 pb-7 border-t border-white/10 pt-5 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="pointer-events-auto"
        >
          <p data-testid="hero-kicker" className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
            Chicago's Finest — Est. by Monica Alexander
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="hero-shop-btn"
              onClick={() => scrollToId("collection")}
              className="bg-white text-black rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.22em] font-semibold transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              Shop the Drop
            </button>
            <button
              data-testid="hero-craft-btn"
              onClick={() => scrollToId("craft")}
              className="border border-white/30 rounded-full px-8 py-3.5 text-xs uppercase tracking-[0.22em] transition-colors duration-300 hover:bg-white hover:text-black"
            >
              The Craft
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.45, duration: 0.9 }}
          className="pointer-events-auto order-first sm:order-none w-full sm:w-auto flex justify-center"
        >
          <Countdown />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="hidden sm:flex flex-col items-center gap-2.5 text-white/50 text-[10px] uppercase tracking-[0.3em] pointer-events-auto cursor-pointer"
          onClick={() => scrollToId("keywords")}
          data-testid="hero-down-btn"
        >
          <PixelChevron />
          Down
        </motion.div>
      </div>
    </section>
  );
}
